import React, { useState, useContext } from 'react';
import { Plus } from '@styled-icons/boxicons-regular';
import Select from 'react-select';
import { User } from 'lib/User';
import styled from 'styled-components';

import { UserContext } from 'constants/user-context';
import { TOTEM_ICONS } from 'constants/totemIcons';

import { TeamPopupWindow } from 'pages/Settings/SettingsPage/SettingsTeam/TeamPopupWindow';

import { OptimizedImage } from 'components/OptimizedImage';

import { buildImgUrl } from 'utils/image/buildImgUrl';

import { SettingsCardSubtitle } from 'styles/pages/settings.styles';

import { TotemFragmentFragment as Totem, useUpdateTeamRoleMutation } from 'data/__generated__';
import type { MinimalUser } from '../SettingsTeam';

// interface Totem {
//     _id: string;
//     number: number;
// }

interface UsersTableProps {
    users: MinimalUser[];
    totems: Totem[] | undefined;
}

export const UsersTable: React.FC<UsersTableProps> = ({ users, totems }) => {
    const { organization } = useContext(UserContext);
    const maxUsers = organization?.maxUsers ?? 0;
    const [teamPopupOpen, setTeamPopupOpen] = useState<boolean>(false);
    const [roleBeingChanged, setRoleBeingChanged] = useState<{ userId: string | undefined; totemId: string }>({
        userId: '',
        totemId: '',
    });
    const [updateTeamRole, { loading }] = useUpdateTeamRoleMutation();

    const handleChangeUserRole = async (role: string | null, userId: string | undefined, totemId: string) => {
        setRoleBeingChanged({ userId, totemId });
        if (!userId) return;
        await updateTeamRole({ variables: { role, userId, totemId }, refetchQueries: ['GET_USERS_OF_ORGANIZATION'] });
    };

    const roleOptions = [
        { value: 'Admin', label: 'Admin' },
        { value: 'Editor', label: 'Éditeur' },
        { value: 'Analyst', label: 'Analyste' },
        { value: null, label: '' },
    ];

    const teamMembers = new Array(maxUsers).fill(1);

    return (
        <>
            <SettingsCardSubtitle $noMargin>
                Mon équipe ({users?.length}/{maxUsers})
            </SettingsCardSubtitle>
            <UsersTableWrapper>
                <UsersTableStyle>
                    <thead>
                        <tr>
                            <th />
                            {teamMembers.map((member, index) => {
                                return <th key={index}>Utilisateur #{index + 1}</th>;
                            })}
                        </tr>
                    </thead>
                    <tbody>
                        <tr>
                            <th />
                            {teamMembers.map((_, i) => {
                                return (
                                    <th key={i}>
                                        <Username>
                                            {users[i]?.username ? (
                                                <>
                                                    <div>{users[i]?.username}</div>
                                                </>
                                            ) : (
                                                <PlusButton
                                                    height="23px"
                                                    width="23px"
                                                    onClick={() => setTeamPopupOpen(true)}
                                                >
                                                    <Plus size="17" />
                                                </PlusButton>
                                            )}
                                        </Username>
                                    </th>
                                );
                            })}
                        </tr>
                        {totems?.map((totem) => {
                            return (
                                <tr key={totem._id}>
                                    <th>
                                        <OptimizedImage
                                            src={buildImgUrl(
                                                `img/illustrations/Totem_illustration-${TOTEM_ICONS[(totem.number - 1) % 6].id}.png`,
                                            )}
                                            width={18}
                                        />{' '}
                                        TOTEM #{totem.number}
                                    </th>
                                    {teamMembers.map((_, i) => {
                                        const selectEnabled = i && User.isInRole('Admin', totem._id);
                                        let currentUserRole = null;
                                        const isLoading =
                                            loading &&
                                            roleBeingChanged.totemId === totem._id &&
                                            roleBeingChanged.userId === users[i]?._id;
                                        if (users[i]) {
                                            const userRoles = User.roleInGroup(totem._id, users[i]?.roles);
                                            if (userRoles && userRoles.length > 0) {
                                                currentUserRole = userRoles[0];
                                            } else {
                                                currentUserRole = null;
                                            }
                                        }
                                        return (
                                            <td key={i}>
                                                {!!users[i] && (
                                                    <Select
                                                        placeholder="Ajuster les droits"
                                                        value={
                                                            roleOptions.find(
                                                                (role) => role.value === currentUserRole,
                                                            ) || { value: null, label: '' }
                                                        }
                                                        isDisabled={isLoading || !selectEnabled}
                                                        options={roleOptions}
                                                        onChange={(option) =>
                                                            handleChangeUserRole(
                                                                option ? option.value : null,
                                                                users[i]?._id,
                                                                totem._id,
                                                            )
                                                        }
                                                    />
                                                )}
                                            </td>
                                        );
                                    })}
                                </tr>
                            );
                        })}
                    </tbody>
                </UsersTableStyle>
            </UsersTableWrapper>
            {teamPopupOpen && <TeamPopupWindow setPopupOpen={setTeamPopupOpen} isEmployee={false} />}
        </>
    );
};

const Username = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
`;

const UsersTableWrapper = styled.div`
    width: 100%;
    overflow-x: auto;
`;

const UsersTableStyle = styled.table`
    position: relative;
    text-align: center;
    width: 100%;
    min-width: 600px;
    thead {
        th {
            text-align: center;
            font-size: 1.1rem;
        }
    }
    th {
        text-align: left;
        font-size: 1.1rem;
    }
`;

type PlusButtonProps = {
    onClick: () => void;
    height?: string;
    width?: string;
};

const PlusButton = styled.button<PlusButtonProps>`
    border-radius: 50%;
    background-color: ${({ theme }) => theme.colors.yellow};
    border: none;
    cursor: pointer;
    height: 23px;
    width: 23px;
    display: inline-grid;
    align-items: center;
    justify-content: center;
`;
